/**
 * FormData → contract input.
 *
 * Every create form posts to its route's action, which reads fields with these
 * helpers, validates with the same zod schema the server uses, and returns
 * field-keyed issues on failure so the form can render them inline. The server
 * validates again — this is for the user's benefit, not the server's.
 */

import type { ZodError } from 'zod'

/** Blank inputs mean "not provided", which the contracts spell as null. */
export function text(form: FormData, key: string): string | null {
  const value = form.get(key)
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export function required(form: FormData, key: string): string {
  return text(form, key) ?? ''
}

export function int(form: FormData, key: string): number | null {
  const value = text(form, key)
  if (value === null) return null
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) ? parsed : null
}

export function decimal(form: FormData, key: string): number | null {
  const value = text(form, key)
  if (value === null) return null
  const parsed = Number.parseFloat(value)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * `<input type="datetime-local">` yields local wall-clock with no zone; the
 * contracts want an ISO instant.
 */
export function datetime(form: FormData, key: string): string | null {
  const value = text(form, key)
  if (value === null) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

/** Optional single-select: the "no selection" option posts an empty string. */
export function choice<T extends string>(
  form: FormData,
  key: string,
  allowed: readonly T[],
): T | null {
  const value = text(form, key)
  return value !== null && (allowed as readonly string[]).includes(value) ? (value as T) : null
}

/**
 * Field-keyed issues, matching the shape the server's ZodPipe returns, so a
 * client-side failure and a server-side one render identically.
 */
export function issuesFrom(error: ZodError): Record<string, string[]> {
  const issues: Record<string, string[]> = {}
  for (const issue of error.issues) {
    const key = issue.path.length > 0 ? issue.path.join('.') : '_'
    const bucket = issues[key]
    if (bucket) bucket.push(issue.message)
    else issues[key] = [issue.message]
  }
  return issues
}

export interface FormResult {
  ok: false
  message?: string
  issues: Record<string, string[]>
}

export const invalid = (issues: Record<string, string[]>, message?: string): FormResult =>
  message === undefined ? { ok: false, issues } : { ok: false, issues, message }
